/**
 * Analytics utility functions for common events
 * These functions provide a convenient way to log analytics events throughout the app
 */

import { logAnalyticsEvent, setAnalyticsUserProperties } from "../firebase";

/**
 * Log when user signs in anonymously
 */
export const logAnonymousSignIn = (): void => {
  logAnalyticsEvent("anonymous_sign_in", {
    timestamp: new Date().toISOString(),
  });
};

/**
 * Log when user sets their name
 */
export const logUserNameSet = (name: string): void => {
  logAnalyticsEvent("user_name_set", {
    timestamp: new Date().toISOString(),
  });
};

/**
 * Log when user joins a room
 */
export const logJoinRoom = (roomId: string, roomName?: string): void => {
  logAnalyticsEvent("join_room", {
    room_id: roomId,
    room_name: roomName || "unknown",
    timestamp: new Date().toISOString(),
  });
};

/**
 * Log when user leaves a room
 */
export const logLeaveRoom = (
  roomId: string,
  durationSeconds?: number,
): void => {
  logAnalyticsEvent("leave_room", {
    room_id: roomId,
    duration_seconds: durationSeconds || 0,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Log when user creates a new room
 */
export const logCreateRoom = (roomName: string, topics?: string[]): void => {
  logAnalyticsEvent("create_room", {
    room_name: roomName,
    topics: topics?.join(",") || "none",
    timestamp: new Date().toISOString(),
  });
};

/**
 * Log when user edits a room
 */
export const logEditRoom = (roomId: string, changedFields: string[]): void => {
  logAnalyticsEvent("edit_room", {
    room_id: roomId,
    changed_fields: changedFields.join(","),
    timestamp: new Date().toISOString(),
  });
};

/**
 * Log when user receives feedback
 */
export const logFeedbackReceived = (
  feedbackType: string,
  value?: number,
): void => {
  logAnalyticsEvent("feedback_received", {
    feedback_type: feedbackType,
    value: value || 0,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Log when user sends feedback
 */
export const logFeedbackSent = (
  feedbackType: string,
  rating?: number,
): void => {
  logAnalyticsEvent("feedback_sent", {
    feedback_type: feedbackType,
    rating: rating || 0,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Log when user enables/disables virtual background
 */
export const logVirtualBackgroundToggle = (enabled: boolean): void => {
  logAnalyticsEvent("virtual_background_toggle", {
    enabled,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Log when user views etiquette rules
 */
export const logViewEtiquette = (): void => {
  logAnalyticsEvent("view_etiquette", {
    timestamp: new Date().toISOString(),
  });
};

/**
 * Set user-level analytics properties
 */
export const setUserAnalyticsProperties = (userId: string): void => {
  setAnalyticsUserProperties({
    user_id: userId,
    app_version: "1.0.0",
  });
};

/**
 * Set user property indicating they have created a room
 */
export const markUserCreatedRoom = (): void => {
  setAnalyticsUserProperties({
    has_created_room: "true",
  });
};

/**
 * Set user property indicating they have joined a room
 */
export const markUserJoinedRoom = (): void => {
  setAnalyticsUserProperties({
    has_joined_room: "true",
  });
};

/**
 * Log screen view
 */
export const logScreenView = (screenName: string): void => {
  logAnalyticsEvent("screen_view", {
    screen_name: screenName,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Log error events
 */
export const logError = (errorType: string, errorMessage: string): void => {
  logAnalyticsEvent("app_error", {
    error_type: errorType,
    error_message: errorMessage,
    timestamp: new Date().toISOString(),
  });
};
